import SelectableDiv from "components/SelectableDiv";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useCustomQuery } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";
import SubLabel from "components/Atoms/Sublabel";
import SupportArticleLink from "supportbeacon/SupportArticleLink";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";

interface Props {
  name: string;
  country: server.dto.Country;
  onChange: (newName: string) => void;
  onGooglePlaceSelected: (place: server.dto.GooglePlace) => void;
  onNorwegianCompanySelected: (company: server.dto.NorwegianCompanyRegistration) => void;
}

type DisplayResultFrom = "googlePlaces" | "norwegianCompanyRegister";

const CompanyNameField = (props: Props) => {
  const [displayResultsFrom, setDisplayResultsFrom] = React.useState<DisplayResultFrom>(props.country.key === "norway" ? "norwegianCompanyRegister" : "googlePlaces");
  // Results are hidden until the first API response comes back
  const [displayResults, setDisplayResults] = React.useState(false);

  // The first API call is made after the debounce time has passed
  const [isSearchEnabled, setIsSearchEnabled] = React.useState(false);

  // We only start making API calls after the user starts typing
  // So not if the `name` prop is changed by the parent component
  const [isTyping, setIsTyping] = React.useState(false);

  const debouncedName = useDebounce(props.name, 500);
  const isDebouncing = debouncedName !== props.name;

  React.useEffect(() => {
    if (props.country.key === "norway") {
      setDisplayResultsFrom("norwegianCompanyRegister");
    } else {
      setDisplayResultsFrom("googlePlaces");
    }
  }, [props.country.key]);

  // If the user starts typing, we enable the Google Places search after the debounce time has passed
  React.useEffect(() => {
    if (!isDebouncing && isTyping && !isSearchEnabled) {
      setIsSearchEnabled(true);
    }
  }, [debouncedName, isTyping, isSearchEnabled])

  const queryKey = ["googlePlacesAutoComplete", debouncedName];
  const norwegianCompanyQueryKey = ["norwegianCompanySearch", debouncedName];

  const query = useCustomQuery({
    queryKey,
    queryFn: () => window.Api.GooglePlaces.Autocomplete(debouncedName).then(r => r.data),
    placeholderData: [],
    keepPreviousData: true,
    enabled: isSearchEnabled && isTyping && debouncedName.length > 2,
    onSuccess: () => {
      setDisplayResults(true);
    }
  });

  const norwegianCompanyQuery = useCustomQuery({
    queryKey: norwegianCompanyQueryKey,
    queryFn: () => window.Api.NorwegianCompanyRegister.TextSearch(debouncedName, 5).then(r => r.data),
    placeholderData: [],
    keepPreviousData: true,
    enabled: props.country.key === "norway" && isSearchEnabled && isTyping && debouncedName.length > 2,
    onSuccess: () => {
      setDisplayResults(true);
    }
  });

  const getGooglePlaceDetails = useMutation((placeId: string) => window.Api.GooglePlaces.GetPlace(placeId, "company").then(r => r.data));

  const onItemClick = async (item: server.dto.GooglePlaceAutocomplete) => {
    if (getGooglePlaceDetails.isLoading) return;

    setIsSearchEnabled(false);
    setDisplayResults(false);
    setIsTyping(false);
    const res = await getGooglePlaceDetails.mutateAsync(item.placeId);
    props.onGooglePlaceSelected(res);
  }

  const onNorwegianCompanyClick = (item: server.dto.NorwegianCompanyRegistration) => {
    setIsSearchEnabled(false);
    setDisplayResults(false);
    setIsTyping(false);
    props.onNorwegianCompanySelected(item);
  }

  const onNameChange = (newName: string) => {
    props.onChange(newName);
    setIsTyping(true);
  }

  const data = displayResultsFrom === "googlePlaces" ? query.data : norwegianCompanyQuery.data;

  return <div className="flex-column gap">
    <div>
      <RequiredTextInput
        focusOnMount
        value={props.name}
        onChange={onNameChange}
        validatorKey="companyName"
        maxLength={serverReference.constants.company.maxNameLength}
      />
    </div>

    {(displayResults && props.country.key === "norway") && <div>
      <RadioGroup
        orientation="horizontal"
        options={[{
          label: "Google Places",
          value: "googlePlaces"
        }, {
          label: "Brønnøysund Register",
          value: "norwegianCompanyRegister"
        }]}
        value={displayResultsFrom}
        onChange={value => setDisplayResultsFrom(value)}
      />
    </div>}

    {displayResults && <div>
      {data.length === 0 && <div>
        <BlueBox>
          {displayResultsFrom === "norwegianCompanyRegister" ?
            <>No match found in the <SupportArticleLink articleId={serverReference.supportArticleIds.norwegianCompanyRegistryIntegration}>Brønnøysund Register</SupportArticleLink></> :
            <>No match found on <SupportArticleLink articleId={serverReference.supportArticleIds.googlePlacesIntegration}>Google Places</SupportArticleLink></>}
        </BlueBox>
      </div>}

      {(data.length > 0 && displayResultsFrom === "googlePlaces") && <>
        {query.data.map((item) => <SelectableDiv
          key={item.placeId}
          group="companyNameSuggestions"
          value={item.name}
          selected={false}
          onClick={() => onItemClick(item)}
        >
          {item.name}
          <SubLabel small>{item.address}</SubLabel>
        </SelectableDiv>)}
        <BlueBox>
          Powered by <SupportArticleLink articleId={serverReference.supportArticleIds.googlePlacesIntegration}>Google Places</SupportArticleLink>
        </BlueBox>
      </>}
      {(data.length > 0 && displayResultsFrom === "norwegianCompanyRegister") && <>
        {norwegianCompanyQuery.data.map((item) => <SelectableDiv
          key={item.businessRegistrationNumber}
          group="companyNameSuggestions"
          value={item.name}
          selected={false}
          onClick={() => onNorwegianCompanyClick(item)}
        >
          {item.name}
          {item.address != null && <SubLabel small>{item.address.addressLine1} {item.address.addressLine2} {item.address.postalCode} {item.address.city}</SubLabel>}
        </SelectableDiv>)}
        <BlueBox>
          Powered by <SupportArticleLink articleId={serverReference.supportArticleIds.norwegianCompanyRegistryIntegration}>Brønnøysund Register</SupportArticleLink>
        </BlueBox>
      </>}
    </div>}
  </div>
}

const BlueBox = (props: React.PropsWithChildren<unknown>) => {
  return <div className="blue-bg white-text s1_small padding" style={{ textAlign: "center" }}>
    {props.children}
  </div>;
}

export default CompanyNameField;